import { Loader2, Star, UserPlus, Users, X } from 'lucide-react';
import FormModal from '../../../components/Modals/FormModal';
import SelectField from '../../../components/Forms/Input/SelectField';
import ToggleSwitch from '../../../components/Forms/Input/ToggleSwitch';
import {
  RestaurantPreparationStationDTO, RestaurantStationAssignmentDTO,
  RestaurantStationAssignmentForm, RestaurantStationUserOption,
} from '../../../types/restaurant/RestaurantPreparationStationTypes';

interface Props {
  isOpen: boolean; station: RestaurantPreparationStationDTO | null;
  assignments: RestaurantStationAssignmentDTO[]; users: RestaurantStationUserOption[];
  form: RestaurantStationAssignmentForm; loading: boolean; saving: boolean;
  onClose: () => void; onChange: <K extends keyof RestaurantStationAssignmentForm>(field: K, value: RestaurantStationAssignmentForm[K]) => void;
  onAssign: () => void; onUpdate: (id: number, data: { primaryAssignment?: boolean; active?: boolean }) => void;
  onDeactivate: (id: number) => void;
}

const StationAssignmentsModal = ({ isOpen, station, assignments, users, form, loading, saving, onClose, onChange, onAssign, onUpdate, onDeactivate }: Props) => {
  const assignedIds = new Set(assignments.filter((a) => a.active).map((a) => a.userId));
  const availableUsers = users.filter((user) => !assignedIds.has(user.id));
  return (
    <FormModal isOpen={isOpen} onClose={onClose} title={`Responsables · ${station?.name ?? ''}`} maxWidth="max-w-3xl">
      <div className="space-y-5">
        <div className="rounded-xl border border-primary/15 bg-primary/5 p-4">
          <div className="mb-3 flex items-center gap-2 font-medium text-black dark:text-white"><UserPlus className="h-5 w-5 text-primary" />Asignar usuario</div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto_auto] md:items-end">
            <SelectField label="Usuario" name="assignmentUserId" value={form.userId} iconKey="user" placeholder="Seleccione un usuario" options={availableUsers.map((u) => ({ value: u.id, label: u.username }))} onChange={(e) => onChange('userId', Number(e.target.value))} />
            <div className="mb-4 rounded border border-stroke px-3 py-3 dark:border-strokedark"><ToggleSwitch label="Principal" name="primaryAssignment" checked={form.primaryAssignment} onChange={() => onChange('primaryAssignment', !form.primaryAssignment)} /></div>
            <button onClick={onAssign} disabled={!form.userId || saving || !station?.active || !station?.enabled} className="mb-4 inline-flex h-[50px] items-center justify-center gap-2 rounded bg-primary px-4 font-medium text-white disabled:opacity-50">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Asignar</button>
          </div>
          {station && (!station.active || !station.enabled) && <p className="text-sm text-warning">Reactive y habilite la estación para asignar usuarios.</p>}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
        ) : assignments.length === 0 ? (
          <div className="py-10 text-center text-bodydark2"><Users className="mx-auto mb-3 h-8 w-8" />No hay usuarios asignados.</div>
        ) : (
          <div className="space-y-2">
            {assignments.map((assignment) => (
              <div key={assignment.id} className={`flex flex-col gap-3 rounded-lg border border-stroke p-4 dark:border-strokedark sm:flex-row sm:items-center sm:justify-between ${assignment.active ? '' : 'opacity-60'}`}>
                <div>
                  <div className="flex items-center gap-2 font-medium text-black dark:text-white">{assignment.userFullName || assignment.username}{assignment.primaryAssignment && <Star className="h-4 w-4 fill-warning text-warning" />}</div>
                  <p className="text-sm text-bodydark2">@{assignment.username} · {assignment.active ? 'Activo' : 'Inactivo'}</p>
                </div>
                <div className="flex gap-2">
                  {assignment.active ? (
                    <>
                      <button onClick={() => onUpdate(assignment.id, { primaryAssignment: !assignment.primaryAssignment, active: true })} className="rounded border border-stroke px-3 py-2 text-sm font-medium hover:border-primary hover:text-primary dark:border-strokedark">{assignment.primaryAssignment ? 'Quitar principal' : 'Hacer principal'}</button>
                      <button onClick={() => onDeactivate(assignment.id)} title="Desactivar asignación" className="rounded border border-stroke p-2 text-danger hover:border-danger dark:border-strokedark"><X className="h-4 w-4" /></button>
                    </>
                  ) : (
                    <button onClick={() => onUpdate(assignment.id, { active: true, primaryAssignment: false })} disabled={!station?.active || !station?.enabled} className="rounded border border-primary px-3 py-2 text-sm font-medium text-primary disabled:opacity-50">Reactivar</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </FormModal>
  );
};

export default StationAssignmentsModal;
